import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon, Platform } from '../components/Icons.jsx';
import { api } from '../services/api/client.js';
import { useContentFeed } from '../hooks/useContentFeed.js';
import { useGenerationStatus } from '../state/GenerationStatusContext.jsx';
import { startContentBatchOperation } from '../lib/operations.js';
import { formatAppDate } from '../utils/locale.js';
import { BatchPreview } from '../components/preview/BatchPreview.jsx';
import { normalizePost } from '../components/preview/normalizePost.js';
import { CreditConfirmationModal } from '../components/CreditConfirmationModal.jsx';
import { useUpgrade } from '../contexts/UpgradeContext.jsx';
import { createPollingClock } from '../utils/pollingClock.js';
import { platformDisplayName } from '../lib/platforms.js';

const TYPES = [
  { id: 'image_post', label: 'Single image post', desc: 'One AI-generated image with platform captions', cost: 2, icon: 'image' },
];

const GOAL_OPTIONS = [
  ['education', 'Educate'],
  ['authority', 'Build authority'],
  ['engagement', 'Drive engagement'],
  ['leads', 'Convert to leads'],
  ['sales', 'Sell'],
  ['visibility', 'Visibility'],
];

const QUICK_SCHEDULES = [
  { id: 'best', label: 'Best time', hint: 'recommended' },
  { id: 'soon', label: 'Publish soon', hint: '~1 min' },
  { id: 'evening', label: 'Today evening', hint: '6:00 PM' },
  { id: 'tomorrow', label: 'Tomorrow morning', hint: '9:00 AM' },
  { id: 'custom', label: 'Pick time', hint: 'custom' },
];

const PACK_OPTIONS = [
  { id: 1, label: '1 post' },
  { id: 3, label: '3 posts' },
  { id: 7, label: '7 days' },
  { id: 14, label: '14 days' },
  { id: 30, label: '30 days' },
  { id: 'custom', label: 'Custom' },
];

const PLATFORMS_AVAILABLE = ['instagram', 'threads', 'linkedin', 'telegram', 'tiktok', 'facebook', 'x'];
const CREATE_PREVIEW_PUBLISH_LOOKAHEAD_MS = 90 * 1000;
const MAX_REFERENCE_FILE_BYTES = 8 * 1024 * 1024;
const POST_PREVIEW_INTENT_KEY = 'post_preview_intent';

export const Create = ({ navigate, openContent, onContentGenerated }) => {
  const { t } = useTranslation();
  const { startGenerationBatch, upsertContentItems, content, refreshContent, activeBatches } = useContentFeed();
  const { startOperation } = useGenerationStatus();
  const { openUpgrade } = useUpgrade();
  const location = useLocation();
  const routerNavigate = useNavigate();
  void onContentGenerated;
  const [type, setType] = useState('image_post');
  const [count, setCount] = useState(1);
  const [customCount, setCustomCount] = useState(10);
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState('Professional');
  const [goal, setGoal] = useState('engagement');
  const [platforms, setPlatforms] = useState(['instagram']);
  const [includeCaption, setIncludeCaption] = useState(true);
  const [includeHashtags, setIncludeHashtags] = useState(true);
  const [includeCTA, setIncludeCTA] = useState(false);
  const [scheduling, setScheduling] = useState('schedule');
  const [schedulePreset, setSchedulePreset] = useState('best');
  const [customScheduleAt, setCustomScheduleAt] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [missingBrandProfile, setMissingBrandProfile] = useState(false);
  const [checkingBrandProfile, setCheckingBrandProfile] = useState(true);
  const [targetAccounts, setTargetAccounts] = useState([]);
  const [platformRegistry, setPlatformRegistry] = useState({});
  const [creditUsage, setCreditUsage] = useState(null);
  const [publishingPrefs, setPublishingPrefs] = useState(null);
  const [selectedTargetIds, setSelectedTargetIds] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(true);
  const [targetLoadError, setTargetLoadError] = useState(null);
  const [previewBatch, setPreviewBatch] = useState(null);
  const [creditConfirmOpen, setCreditConfirmOpen] = useState(false);
  const [lastPreviewSession, setLastPreviewSession] = useState(null);
  const [resumingBatch, setResumingBatch] = useState(false);
  const [onboardingPrefillLoaded, setOnboardingPrefillLoaded] = useState(false);
  const [contentPlan, setContentPlan] = useState(null);
  const [contentPlanChecked, setContentPlanChecked] = useState(false);
  const [contentPlanMissing, setContentPlanMissing] = useState(false);
  const [referenceOpen, setReferenceOpen] = useState(false);
  const [referenceFiles, setReferenceFiles] = useState([]);
  const [referenceContexts, setReferenceContexts] = useState([]);
  const [referenceBusy, setReferenceBusy] = useState(false);
  const [referenceError, setReferenceError] = useState(null);
  const routeBatchId = useMemo(() => new URLSearchParams(location.search).get('batch') || null, [location.search]);

  useEffect(() => {
    const rawOnboardingPrefill = sessionStorage.getItem('onboarding_first_post_prefill');
    if (rawOnboardingPrefill) {
      sessionStorage.removeItem('onboarding_first_post_prefill');
      try {
        const prefill = JSON.parse(rawOnboardingPrefill);
        if (typeof prefill.topic === 'string' && prefill.topic.trim()) setTopic(prefill.topic.trim());
        if (GOAL_OPTIONS.some(([value]) => value === prefill.goal)) setGoal(prefill.goal);
        if (Array.isArray(prefill.platforms) && prefill.platforms.length) setPlatforms(normalizePlatforms(prefill.platforms));
        if (Number(prefill.count) > 1) {
          setCount('custom');
          setCustomCount(clampCount(prefill.count));
        } else {
          setCount(1);
        }
        setScheduling('draft');
        setAdvancedOpen(true);
        setOnboardingPrefillLoaded(true);
      } catch {
        setOnboardingPrefillLoaded(false);
      }
    }

    const rawPrefill = sessionStorage.getItem('scheduler_create_prefill');
    if (!rawPrefill) return;
    sessionStorage.removeItem('scheduler_create_prefill');
    try {
      const prefill = JSON.parse(rawPrefill);
      if (prefill.scheduledAt && !Number.isNaN(Date.parse(prefill.scheduledAt))) {
        setScheduling('schedule');
        setSchedulePreset('custom');
        setCustomScheduleAt(toDatetimeLocalValue(prefill.scheduledAt));
        setAdvancedOpen(true);
      }
    } catch {
      // Ignore stale prefill data.
    }
  }, []);

  const selectedType = TYPES.find(item => item.id === type) || TYPES[0];
  const normalizedPlatforms = normalizePlatforms(platforms);
  const generationCount = count === 'custom' ? clampCount(customCount) : count;
  // A generated post counts as 1 against the monthly Posts limit, regardless of
  // how many platforms it publishes to (no credits — a concrete post limit).
  const estimatedPosts = Math.max(1, generationCount);
  const generateLabel = checkingBrandProfile ? t('create.generateLabelChecking') : generating ? t('create.generateLabelGenerating') : generationCount > 1 ? t('create.generateLabelCount', { value: generationCount }) : t('create.generateLabelSingle');
  const publishScheduleAt = scheduling === 'draft' ? null : scheduleDateForPreset(schedulePreset, customScheduleAt);
  const targetRows = targetAccounts
    .map(account => {
      const platform = normalizePlatform(account.platform);
      const displayName = displayAccountName(account);
      const blockedReason = publishTargetBlockedReason(account, platformRegistry);
      return {
        id: account.id,
        platform,
        name: displayName,
        secondaryText: accountSecondaryText(account, displayName),
        capabilities: account.capabilities || {},
        externalAccountId: account.externalAccountId,
        ready: !blockedReason,
        blockedReason,
      };
    });
  const selectedReadyTargets = targetRows.filter(row => row.ready && selectedTargetIds.includes(row.id));
  const previewPlannedPlatforms = previewBatch ? normalizePlatforms(previewBatch.platforms?.length ? previewBatch.platforms : normalizedPlatforms) : normalizedPlatforms;
  const previewPlannedCount = previewBatch ? plannedCountForBatch(previewBatch, generationCount) : generationCount;
  const previewTopic = previewBatch?.topic || topic;
  const previewTargets = previewBatch?.targetAccountIds?.length
    ? targetRows.filter(row => previewBatch.targetAccountIds.includes(row.id))
    : selectedReadyTargets;
  const willSchedule = scheduling !== 'draft';
  const missingScheduleTarget = willSchedule && selectedReadyTargets.length === 0;
  const generateDisabled = generating || checkingBrandProfile || missingBrandProfile || !topic.trim() || !type || normalizedPlatforms.length === 0 || missingScheduleTarget;
  const resumeSession = useMemo(
    () => lastPreviewSession ? deriveResumeSession(lastPreviewSession, content) : null,
    [lastPreviewSession, content],
  );
  const contentPlanSuggestions = useMemo(() => contentPlanSuggestionItems(contentPlan), [contentPlan]);
  const detectedReferenceUrls = useMemo(() => extractHttpUrls(topic), [topic]);
  const referenceUrls = useMemo(() => uniqueStrings([...referenceContexts.map(item => item.url).filter(Boolean), ...detectedReferenceUrls]), [referenceContexts, detectedReferenceUrls]);
  const referenceAssetIds = useMemo(() => referenceContexts.map(item => item.assetId).filter(Boolean), [referenceContexts]);

  useEffect(() => {
    let cancelled = false;
    async function loadBrandProfile() {
      setCheckingBrandProfile(true);
      try {
        await api.profile.getBrandProfile();
        if (!cancelled) setMissingBrandProfile(false);
      } catch (err) {
        if (!cancelled && isMissingBrandProfileError(err)) {
          setMissingBrandProfile(true);
        }
      } finally {
        if (!cancelled) setCheckingBrandProfile(false);
      }
    }
    loadBrandProfile();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadContentPlan() {
      try {
        const response = await api.strategy.get();
        if (cancelled) return;
        setContentPlan(response.strategy || null);
        setContentPlanMissing(false);
      } catch (err) {
        if (cancelled) return;
        setContentPlan(null);
        setContentPlanMissing(err?.status === 404);
      } finally {
        if (!cancelled) setContentPlanChecked(true);
      }
    }
    loadContentPlan();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadPublishTargets() {
      setLoadingTargets(true);
      setTargetLoadError(null);
      try {
        const [accountResponse, registryResponse] = await Promise.all([
          api.socialAccounts.list(),
          api.platforms.registry(),
        ]);
        if (cancelled) return;
        setPlatformRegistry(Object.fromEntries((registryResponse.platforms || []).map(item => [normalizePlatform(item.platform), item])));
        setTargetAccounts(accountResponse.accounts || []);
      } catch (err) {
        if (!cancelled) {
          setTargetAccounts([]);
          setTargetLoadError(err.message || t('create.errorLoadTargets'));
        }
      } finally {
        if (!cancelled) setLoadingTargets(false);
      }
    }
    loadPublishTargets();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadPlanUsage() {
      try {
        const response = await api.billing.getEntitlements();
        if (!cancelled) setCreditUsage(response.usage?.publish_ready_posts_monthly || null);
      } catch {
        if (!cancelled) setCreditUsage(null);
      }
    }
    loadPlanUsage();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadPublishingPreferences() {
      try {
        const response = await api.profile.getPublishingPreferences();
        if (!cancelled) setPublishingPrefs(response.publishingPreferences || null);
      } catch {
        if (!cancelled) setPublishingPrefs(null);
      }
    }
    loadPublishingPreferences();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (loadingTargets) return;
    setSelectedTargetIds(previous => defaultSelectedTargetIds(targetRows, previous, normalizedPlatforms));
  }, [loadingTargets, normalizedPlatforms.join('|'), targetAccounts, platformRegistry]);

  useEffect(() => {
    if (!routeBatchId) return undefined;
    if (previewBatch?.id === routeBatchId) return undefined;

    const existingBatch = activeBatches.find(batch => batch.id === routeBatchId)
      || content.find(item => item.generationBatchId === routeBatchId)?.batch
      || null;
    if (existingBatch) {
      setLastPreviewSession(session => session?.batch?.id === routeBatchId ? session : null);
      setPreviewBatch(existingBatch);
      setResumingBatch(false);
      return undefined;
    }

    let cancelled = false;
    setResumingBatch(true);
    api.content.getBatch(routeBatchId)
      .then(result => {
        if (cancelled) return;
        if (result?.batch) {
          startGenerationBatch(result.batch, []);
          setLastPreviewSession(null);
          setPreviewBatch(result.batch);
          setError(null);
        } else {
          setError(t('create.errorResumePreview'));
        }
      })
      .catch(err => {
        if (!cancelled) setError(err.message || t('create.errorResumePreview'));
      })
      .finally(() => {
        if (!cancelled) setResumingBatch(false);
      });
    return () => {
      cancelled = true;
    };
  }, [routeBatchId, previewBatch?.id, activeBatches, content]);

  const togglePlatform = (platform) => {
    const platformTargetIds = targetRows
      .filter(row => row.ready && row.platform === platform)
      .map(row => row.id);
    if (platforms.includes(platform)) {
      setPlatforms(value => value.filter(item => item !== platform));
      setSelectedTargetIds(value => value.filter(id => !platformTargetIds.includes(id)));
      return;
    }
    setPlatforms(value => normalizePlatforms([...value, platform]));
    setSelectedTargetIds(value => normalizeIds([...value, ...platformTargetIds]));
  };

  const toggleTarget = (id) => {
    const account = targetRows.find(row => row.id === id);
    const willSelect = !selectedTargetIds.includes(id);
    if (willSelect && account) {
      setPlatforms(value => value.includes(account.platform) ? value : [...value, account.platform]);
    }
    setSelectedTargetIds(value => value.includes(id) ? value.filter(item => item !== id) : [...value, id]);
  };

  const applyContentPlanSuggestion = (suggestion) => {
    if (!suggestion?.topic) return;
    setTopic(suggestion.topic);
    if (isKnownGoal(suggestion.goal)) setGoal(suggestion.goal);
    if (suggestion.platform && PLATFORMS_AVAILABLE.includes(suggestion.platform)) {
      setPlatforms(value => normalizePlatforms([...value, suggestion.platform]));
    }
  };

  const addReferenceFiles = (files) => {
    const nextFiles = Array.from(files || []).filter(file => /^image\//i.test(file.type || ''));
    setReferenceError(null);
    if (!nextFiles.length) {
      setReferenceError(t('create.referenceErrorImagesOnly'));
      return;
    }
    const oversized = nextFiles.find(file => file.size > MAX_REFERENCE_FILE_BYTES);
    if (oversized) {
      setReferenceError(t('create.referenceErrorTooLarge'));
      return;
    }
    setReferenceFiles(value => uniqueFiles([...value, ...nextFiles]).slice(0, 8));
  };

  const handleTopicPaste = (event) => {
    const files = Array.from(event.clipboardData?.files || []).filter(file => /^image\//i.test(file.type || ''));
    if (!files.length) return;
    event.preventDefault();
    addReferenceFiles(files);
    setReferenceOpen(true);
  };

  const handleReferenceDrop = (event) => {
    event.preventDefault();
    addReferenceFiles(event.dataTransfer?.files);
  };

  const uploadReferenceFiles = async () => {
    if (referenceBusy) return;
    setReferenceError(null);
    if (!referenceFiles.length) {
      setReferenceError(t('create.referenceErrorChooseOne'));
      return;
    }
    try {
      setReferenceBusy(true);
      const saved = [];
      for (const file of referenceFiles) {
        const result = await api.profile.addBrandReference({
          type: 'product',
          notes: 'Image added in Create',
          fileName: file.name,
          mimeType: file.type,
          dataUrl: await readFileAsDataUrl(file),
        });
        const assetId = result.reference?.assetId;
        if (!assetId) throw new Error(t('create.referenceErrorNoAssetId'));
        saved.push({
          id: assetId,
          kind: 'image_reference',
          label: t('create.referenceLabelImageAdded'),
          assetId,
          fileName: file.name,
          publicUrl: result.reference?.publicUrl || null,
        });
      }
      setReferenceContexts(value => uniqueReferenceContexts([...value, ...saved]));
      setReferenceFiles([]);
    } catch (err) {
      setReferenceError(err.message || t('create.referenceErrorSave'));
    } finally {
      setReferenceBusy(false);
    }
  };

  const removeReferenceFile = (name, index) => {
    setReferenceFiles(value => value.filter((file, fileIndex) => !(file.name === name && fileIndex === index)));
  };

  const removeReferenceContext = (id) => {
    setReferenceContexts(value => value.filter(item => item.id !== id));
  };

  const onGenerate = async () => {
    if (generateDisabled) return;
    setCreditConfirmOpen(true);
  };

  const confirmGenerate = async () => {
    setGenerating(true);
    setCreditConfirmOpen(false);
    setError(null);
    setMissingBrandProfile(false);
    try {
      const generationPlatforms = normalizePlatforms([...platforms, ...selectedReadyTargets.map(target => target.platform)]);
      const result = await api.content.createBatch({
        topic: topic.trim(),
        goal,
        format: type,
        formats: [type],
        platforms: generationPlatforms,
        referenceUrls,
        referenceAssetIds,
        contextNote: null,
        // useContentPlan removed — backend defaults to true. Content
        // plan is always context for every generation across all
        // entry points (CreatePost, AI Assistant, Autopilot,
        // Weekly Plan re-fill).
        count: generationCount,
        targetAccountIds: selectedReadyTargets.map(target => target.id),
        scheduleMode: scheduling,
        scheduleStartAt: publishScheduleAt?.toISOString(),
        scheduleTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        autopilotMode: publishingPrefs?.autopilotMode || null,
        confirmCreditSpend: true,
      });
      startGenerationBatch(result.batch, result.items || []);
      // Register the batch with the global status plate so the user
      // sees progress across navigation. The App.jsx bridge mirrors
      // ContentFeed's activeBatches into this op.
      if (result.batch?.id) {
        try {
          startContentBatchOperation(startOperation, {
            batchId: result.batch.id,
            total: generationCount,
            platforms: generationPlatforms,
          });
        } catch (_) { /* best-effort */ }
      }
      setLastPreviewSession(null);
      setPreviewBatch(result.batch);
      if (result.batch?.id) navigate('create', { batchId: result.batch.id, replace: true });
    } catch (err) {
      if (isMissingBrandProfileError(err)) {
        setMissingBrandProfile(true);
        setError(null);
      } else if (err.status === 429) {
        const retryAfter = Number(err.details?.retryAfterSeconds);
        setError(retryAfter > 0 ? t('create.errorBusyRetry', { seconds: retryAfter }) : t('create.errorBusy'));
      } else {
        setError(err.message || t('create.errorGenerate'));
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleClosePreview = (session = null) => {
    if (session) setLastPreviewSession(session);
    setPreviewBatch(null);
    if (routeBatchId) navigate('create', { replace: true });
  };
  const handleViewInLibrary = (session = null) => {
    const activeSession = session || lastPreviewSession;
    const rawPosts = (activeSession?.posts || []).map(post => post?.raw).filter(Boolean);
    if (rawPosts.length) {
      upsertContentItems?.(rawPosts, activeSession?.batch || previewBatch || null);
    }
    const filter = libraryFilterForPreview(activeSession, previewBatch, content);
    navigate('content', filter ? { filter } : undefined);
    refreshContent?.({ background: true });
  };
  const handleOpenPostInPreview = (post, intent = null) => {
    const raw = post?.raw;
    const contentId = raw?.contentItem?.id;
    if (intent?.mode && contentId && typeof window !== 'undefined') {
      window.sessionStorage.setItem(POST_PREVIEW_INTENT_KEY, JSON.stringify({ contentId, mode: intent.mode }));
    }
    if (raw && openContent) {
      const batchId = previewBatch?.id || post?.batchId || raw?.contentItem?.generationBatchId || raw?.generationBatch?.id;
      openContent(raw, batchId ? {
        backScreen: 'create',
        backOptions: { batchId },
        backLabel: isContentPlanPreviewBatch(previewBatch || raw?.generationBatch) ? t('create.backToWeekPosts') : t('create.backToGeneratedPosts'),
      } : undefined);
      return;
    }
    if (raw) {
      sessionStorage.setItem('selected_content', JSON.stringify(raw));
    }
    navigate('preview');
  };

  if (previewBatch) {
    return (
      <PreviewPanel
        key={previewBatch.id}
        batch={previewBatch}
        initialSession={lastPreviewSession?.batch?.id === previewBatch.id ? lastPreviewSession : null}
        plannedPlatforms={previewPlannedPlatforms}
        plannedCount={previewPlannedCount}
        topic={previewTopic}
        content={content}
        targets={previewTargets}
        refreshContent={refreshContent}
        onClose={handleClosePreview}
        onViewInLibrary={handleViewInLibrary}
        onOpenPost={handleOpenPostInPreview}
        onSessionChange={setLastPreviewSession}
        onBatchChange={startGenerationBatch}
      />
    );
  }

  return (
    <div className="page create-page-v3">
      <div className="create-hero">
        <div className="create-hero-head">
          <div>
            <h2 className="h2">{t('create.title')}</h2>
            <div className="caption" style={{ marginTop: 4 }}>{t('create.subtitle')}</div>
          </div>
          {creditUsage && (
            <span className="chip chip-active">{t('create.postsLeft', { value: creditLabel(creditUsage) })}</span>
          )}
        </div>

        {missingBrandProfile && (
          <div className="auth-note" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span className="row" style={{ gap: 8 }}><Icon name="alert" size={14} /> {t('create.missingBrandProfile')}</span>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('brand')}>{t('create.fillBrandProfile')}</button>
          </div>
        )}
        {onboardingPrefillLoaded && (
          <div className="auth-note">
            <Icon name="sparkles" size={14} /> {t('create.onboardingPrefillLoaded')}
          </div>
        )}
        {error && <div className="auth-error"><Icon name="alert" size={14} /> {error}</div>}
        {resumingBatch && <div className="auth-note"><Icon name="refresh" size={14} /> {t('create.openingPreview')}</div>}
        <CreditConfirmationModal
          open={creditConfirmOpen}
          title={t('create.confirmTitle')}
          description={t('create.confirmDescription')}
          creditCost={estimatedPosts}
          creditUsage={creditUsage}
          confirming={generating}
          onConfirm={confirmGenerate}
          onCancel={() => setCreditConfirmOpen(false)}
          onBuyCredits={() => openUpgrade({ feature: 'quota' })}
          onUpgrade={() => openUpgrade({ feature: 'quota' })}
        />

        {resumeSession && (
          <PreviewResumeCard
            session={resumeSession}
            onResume={() => {
              setLastPreviewSession(resumeSession);
              setPreviewBatch(resumeSession.batch);
            }}
            onDismiss={() => setLastPreviewSession(null)}
            onViewInLibrary={handleViewInLibrary}
          />
        )}

        <div className="create-hero-step create-topic-step">
          <div className="step-num">1</div>
          <div className="step-body">
            <div className="label">{t('create.topicLabel')}</div>
            <textarea
              className="textarea topic-idea-input"
              rows={2}
              value={topic}
              onChange={event => setTopic(event.target.value)}
              onPaste={handleTopicPaste}
              placeholder={t('create.topicPlaceholder')}
            />
            {contentPlanSuggestions.length > 0 && (
              <div className="topic-suggestion-row" style={{ marginTop: 6 }}>
                <span className="caption"><Icon name="sparkles" size={10} /> {t('create.fromContentPlan')}</span>
                {contentPlanSuggestions.map((suggestion, index) => (
                  <button
                    key={`${suggestion.topic}-${index}`}
                    className="topic-suggestion-chip"
                    title={suggestion.topic}
                    onClick={() => applyContentPlanSuggestion(suggestion)}
                  >
                    {suggestion.label}
                  </button>
                ))}
              </div>
            )}
            {contentPlanChecked && contentPlanMissing && (
              <div className="auth-note" style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 240 }}>
                  <Icon name="sparkles" size={14} />
                  <span>
                    <strong style={{ display: 'block' }}>{t('create.smarterIdeasTitle')}</strong>
                    <span className="caption">{t('create.smarterIdeasDesc')}</span>
                  </span>
                </span>
                <button className="btn btn-primary btn-sm" onClick={() => routerNavigate('/app/plan?tab=plan')}>{t('create.generateContentPlan')}</button>
              </div>
            )}
            <div className="auth-note" style={{ marginTop: 8 }}>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setReferenceOpen(open => !open)}
                aria-expanded={referenceOpen}
              >
                <Icon name={referenceOpen ? 'chev_d' : 'image'} size={13} /> {t('create.addImages')}
              </button>
              <span className="caption" style={{ marginLeft: 8 }}>{t('create.addImagesHint')}</span>

              {(referenceContexts.length > 0 || detectedReferenceUrls.length > 0) && (
                <div className="topic-suggestion-row" style={{ marginTop: 8 }}>
                  {detectedReferenceUrls.map(url => (
                    <span key={url} className="topic-suggestion-chip" title={url}>
                      {t('create.linkFound', { host: referenceUrlHost(url) })}
                    </span>
                  ))}
                  {referenceContexts.map(item => (
                    <button key={item.id} type="button" className="topic-suggestion-chip" onClick={() => removeReferenceContext(item.id)} title={t('create.removeReference')}>
                      {item.label}{referenceContextSuffix(item)} <Icon name="x" size={10} />
                    </button>
                  ))}
                </div>
              )}

              {referenceOpen && (
                <div
                  className="create-image-dropzone"
                  onDrop={handleReferenceDrop}
                  onDragOver={event => event.preventDefault()}
                >
                  <Icon name="image" size={20} />
                  <div>
                    <strong>{t('create.dropImagesHere')}</strong>
                    <span>{t('create.dropImagesFormats')}</span>
                  </div>
                  <input
                    className="input"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    multiple
                    onChange={event => {
                      addReferenceFiles(event.target.files);
                      event.target.value = '';
                    }}
                    disabled={referenceBusy}
                  />
                  {referenceFiles.length > 0 && (
                    <div className="topic-suggestion-row">
                      {referenceFiles.map((file, index) => (
                        <button key={`${file.name}-${index}`} type="button" className="topic-suggestion-chip" onClick={() => removeReferenceFile(file.name, index)} title={t('create.removeImage')}>
                          {file.name} <Icon name="x" size={10} />
                        </button>
                      ))}
                    </div>
                  )}
                  {referenceError && <div className="auth-error"><Icon name="alert" size={14} /> {referenceError}</div>}
                  <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={uploadReferenceFiles} disabled={referenceBusy || !referenceFiles.length}>
                      {referenceBusy ? t('create.addingImages') : t('create.useImages')}
                    </button>
                    <span className="caption">{t('create.imagesOptional')}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="create-hero-step create-platform-step">
          <div className="step-num">2</div>
          <div className="step-body">
            <div className="label">{t('create.whereToPost')}</div>
            <div className="caption" style={{ marginBottom: 8 }}>{t('create.whereToPostHint')}</div>
            <div className="platform-row">
              {PLATFORMS_AVAILABLE.map(platform => (
                <button key={platform} className={'platform-btn ' + (platforms.includes(platform) ? 'on' : '')} onClick={() => togglePlatform(platform)}>
                  <Platform name={platform} size={18} />
                  <span style={{ fontSize: 12.5, fontWeight: 600, textTransform: 'capitalize' }}>{platformDisplayName(platform)}</span>
                  {platforms.includes(platform) && <Icon name="check" size={13} />}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="create-hero-step create-count-step">
          <div className="step-num">3</div>
          <div className="step-body">
            <div className="label">{t('create.howManyPosts')}</div>
            <div className="count-row">
              {PACK_OPTIONS.map(option => (
                <button
                  key={option.id}
                  className={'count-chip ' + (count === option.id ? 'on' : '')}
                  onClick={() => setCount(option.id)}
                >
                  {t(`create.pack.${option.id}`)}
                </button>
              ))}
            </div>
            {count === 'custom' && (
              <input
                className="input"
                type="number"
                min="1"
                max="30"
                value={customCount}
                onChange={event => setCustomCount(event.target.value)}
                style={{ marginTop: 10, maxWidth: 180 }}
              />
            )}
          </div>
        </div>

        <button
          type="button"
          className="advanced-toggle"
          onClick={() => setAdvancedOpen(open => !open)}
          aria-expanded={advancedOpen}
        >
          <Icon name={advancedOpen ? 'chev_d' : 'chev_r'} size={14} />
          {t('create.advancedOptions')}
          <span className="advanced-hint">{t('create.advancedHint')}</span>
        </button>

        {advancedOpen && (
          <div className="create-advanced">
            <div className="form-section">
              <div className="label">{t('create.format')}</div>
              <div className="create-type-grid-v2">
                {TYPES.map(item => (
                  <button key={item.id} className={'create-type-card-v2 ' + (type === item.id ? 'on' : '')} onClick={() => setType(item.id)}>
                    <div className="type-icon"><Icon name={item.icon} size={18} /></div>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{ fontWeight: 650, fontSize: 13 }}>{t(`create.type.${item.id}.label`)}</div>
                      <div className="caption">{t(`create.type.${item.id}.desc`)}</div>
                    </div>
                    <span className="chip" style={{ fontSize: 10 }}>{t('create.creditCost', { value: item.cost })}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid-2-eq create-field-grid-v2">
              <div className="form-section">
                <div className="label">{t('create.toneLabel')}</div>
                <select className="select" value={tone} onChange={event => setTone(event.target.value)}>
                  <option value="Professional">{t('create.tone.professional')}</option>
                  <option value="Bold viral">{t('create.tone.boldViral')}</option>
                  <option value="Story-driven">{t('create.tone.storyDriven')}</option>
                  <option value="Educational">{t('create.tone.educational')}</option>
                  <option value="Casual & friendly">{t('create.tone.casualFriendly')}</option>
                </select>
              </div>
              <div className="form-section">
                <div className="label">{t('create.goalLabel')}</div>
                <select className="select" value={goal} onChange={event => setGoal(event.target.value)}>
                  {GOAL_OPTIONS.map(([value]) => <option key={value} value={value}>{t(`create.goal.${value}`)}</option>)}
                </select>
              </div>
            </div>

            <div className="form-section">
              <div className="row between" style={{ alignItems: 'flex-start', gap: 12 }}>
                <div>
                  <div className="label">{t('create.connectedAccounts')}</div>
                  <div className="caption">{t('create.connectedAccountsHint')}</div>
                </div>
                <span className="chip chip-active">{t('create.selectedCount', { value: selectedReadyTargets.length })}</span>
              </div>
              {targetLoadError && <div className="auth-error" style={{ marginTop: 10 }}><Icon name="alert" size={14} /> {targetLoadError}</div>}
              {loadingTargets ? (
                <div className="publish-target-empty">{t('create.loadingTargets')}</div>
              ) : targetRows.length ? (
                <div className="publish-target-grid">
                  {targetRows.map(account => {
                    const selected = selectedTargetIds.includes(account.id);
                    return (
                      <button key={account.id} className={'publish-target-card ' + (selected ? 'on ' : '') + (!account.ready ? 'is-disabled' : '')} disabled={!account.ready} onClick={() => toggleTarget(account.id)}>
                        <Platform name={account.platform} size={22} />
                        <div className="grow" style={{ textAlign: 'left', minWidth: 0 }}>
                          <div style={{ fontWeight: 650, fontSize: 13 }}>{account.name}</div>
                          <div className="caption">{account.blockedReason || account.secondaryText}</div>
                        </div>
                        {selected && <Icon name="check" size={14} />}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="publish-target-empty">{t('create.noConnectedAccounts')}</div>
              )}
              {missingScheduleTarget && <div className="auth-note" style={{ marginTop: 10 }}><Icon name="alert" size={14} /> {t('create.missingScheduleTarget')}</div>}
            </div>

            <div className="form-section">
              <div className="label">{t('create.include')}</div>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <Toggle val={includeCaption} on={() => setIncludeCaption(value => !value)} label={t('create.includeCaption')} />
                <Toggle val={includeHashtags} on={() => setIncludeHashtags(value => !value)} label={t('create.includeHashtags')} />
                <Toggle val={includeCTA} on={() => setIncludeCTA(value => !value)} label={t('create.includeCTA')} />
              </div>
            </div>

            <div className="form-section">
              <div className="label">{t('create.whenToPublish')}</div>
              <div className="dt-tabs">
                {['draft', 'now', 'schedule'].map((key) => (
                  <button key={key} className={'dt-tab ' + (scheduling === key ? 'is-active' : '')} onClick={() => {
                    setScheduling(key);
                    if (key === 'now') setSchedulePreset('soon');
                    if (key === 'schedule' && schedulePreset === 'soon') setSchedulePreset('best');
                  }}>{t(`create.scheduleTab.${key}`)}</button>
                ))}
              </div>
              {scheduling !== 'draft' && (
                <div className="quick-schedule-grid">
                  {QUICK_SCHEDULES.map(item => (
                    <button
                      key={item.id}
                      className={'quick-schedule-card ' + (schedulePreset === item.id ? 'on' : '')}
                      onClick={() => {
                        setSchedulePreset(item.id);
                        setScheduling(item.id === 'soon' ? 'now' : 'schedule');
                      }}
                    >
                      <span>{t(`create.quickSchedule.${item.id}.label`)}</span>
                      <small>{t(`create.quickSchedule.${item.id}.hint`)}</small>
                    </button>
                  ))}
                </div>
              )}
              {scheduling !== 'draft' && schedulePreset === 'custom' && (
                <input
                  className="input"
                  type="datetime-local"
                  value={customScheduleAt}
                  onChange={event => setCustomScheduleAt(event.target.value)}
                  min={toDatetimeLocalValue(new Date(Date.now() + 60 * 1000))}
                  style={{ marginTop: 10 }}
                />
              )}
            </div>
          </div>
        )}

        <div className="create-hero-cta">
          <div className="create-hero-cta-summary">
            <span className="chip chip-active"><Icon name="sparkles" size={11} /> {t('create.postCount', { count: generationCount })}</span>
            <span className="caption">{t('create.ctaSummary', { posts: t('create.postCount', { count: generationCount }), format: t(`create.type.${selectedType.id}.label`), platforms: t('create.platformCount', { count: normalizedPlatforms.length }) })}</span>
          </div>
          <button className={'btn btn-primary btn-lg generate-button ' + (generating ? 'is-loading' : '')} onClick={onGenerate} disabled={generateDisabled}>
            <Icon name={generating ? 'refresh' : 'sparkles'} size={14} /> {generateLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

function PreviewPanel({ batch, initialSession, plannedPlatforms, plannedCount, topic, content, targets, refreshContent, onClose, onViewInLibrary, onOpenPost, onSessionChange, onBatchChange }) {
  const { t } = useTranslation();
  const stablePostsRef = React.useRef(initialSession?.posts || []);
  const stableBatchRef = React.useRef(initialSession?.batch || batch);
  const frozenRef = React.useRef(Boolean(initialSession?.terminal));
  const refreshContentRef = React.useRef(refreshContent);
  const onBatchChangeRef = React.useRef(onBatchChange);
  const liveBatch = content.find(item => item.generationBatchId === batch.id)?.batch || batch;
  const livePosts = useMemo(() => {
    const filtered = content.filter(item => item.generationBatchId === batch.id);
    const sorted = [...filtered].sort((a, b) => {
      const tA = Date.parse(a.createdAt || 0);
      const tB = Date.parse(b.createdAt || 0);
      return tA - tB;
    });
    const normalized = sorted.map((item, index) => {
      const post = normalizePost(item.raw || item);
      if (post?.status === 'generating') {
        post.generationProgress = Math.max(Number(post.generationProgress || 0), pendingPostProgress(liveBatch, index, plannedCount));
      }
      return post;
    });
    const merged = mergeStablePreviewPosts(stablePostsRef.current, normalized);
    stablePostsRef.current = merged;
    stableBatchRef.current = mergePreviewBatch(stableBatchRef.current, liveBatch);
    const displayPosts = [...(merged.length ? merged : normalized)];
    while (displayPosts.length < plannedCount) {
      const index = displayPosts.length;
      const pendingProgress = pendingPostProgress(liveBatch, index, plannedCount);
      displayPosts.push({
        id: `pending-${index}`,
        title: t('create.pendingPostTitle', { number: index + 1 }),
        format: liveBatch?.formats?.[0] || 'image_post',
        status: 'generating',
        generationProgress: pendingProgress,
        platformVersions: {},
        hashtags: [],
        slides: (liveBatch?.formats?.[0] || 'image_post') === 'carousel' ? [
          { id: `pending-${index}-1`, label: t('create.slideLabelHook'), headline: topic || batch.topic || t('create.generatingCarousel'), sub: t('create.slideNumber', { number: 1 }), order: 0 },
          { id: `pending-${index}-2`, label: t('create.slideLabelTakeaway'), headline: t('create.preparingSlideVisuals'), sub: t('create.slideNumber', { number: 2 }), order: 1 },
        ] : [],
      });
    }
    return displayPosts.slice(0, Math.max(plannedCount, displayPosts.length));
  }, [content, batch.id, batch.topic, liveBatch?.formats?.join('|'), liveBatch?.status, liveBatch?.progress, liveBatch?.readyCount, liveBatch?.generatingCount, liveBatch?.failedCount, liveBatch?.totalItems, plannedCount, topic]);

  const readyCount = livePosts.filter(post => ['ready', 'scheduled', 'publishing', 'published'].includes(post.status)).length;
  const failedCount = livePosts.filter(post => post.status === 'failed').length;
  const displayBatch = stableBatchRef.current || liveBatch;
  const progress = previewProgress(displayBatch, livePosts, plannedCount);
  const statusLabel = previewBatchStatusLabel(displayBatch, livePosts);
  const isTerminal = previewIsTerminal(displayBatch, livePosts, plannedCount);
  if (isTerminal) frozenRef.current = true;
  const session = useMemo(() => ({
    batch: displayBatch,
    topic: topic || batch.topic || t('create.newBatch'),
    statusLabel,
    progress,
    readyCount,
    failedCount,
    plannedCount,
    posts: livePosts,
    terminal: isTerminal,
    updatedAt: new Date().toISOString(),
  }), [displayBatch, topic, batch.topic, statusLabel, progress, readyCount, failedCount, plannedCount, livePosts, isTerminal]);

  useEffect(() => {
    onSessionChange?.(session);
  }, [session, onSessionChange]);

  useEffect(() => {
    refreshContentRef.current = refreshContent;
    onBatchChangeRef.current = onBatchChange;
  }, [refreshContent, onBatchChange]);

  useEffect(() => {
    if (!batch.id || frozenRef.current) return undefined;
    let stopped = false;
    const refreshPreview = () => {
      refreshContentRef.current?.({ background: true });
      api.content.getBatch(batch.id)
        .then(result => {
          if (!stopped && result?.batch) onBatchChangeRef.current?.(result.batch, []);
        })
        .catch(() => {});
    };
    refreshPreview();
    const stop = createPollingClock(() => {
      if (frozenRef.current) {
        stop();
        return;
      }
      refreshPreview();
    }, { minIntervalMs: 4000, maxIntervalMs: 60_000, fireImmediately: false });
    return () => {
      stopped = true;
      stop();
    };
  }, [batch.id]);

  return (
    <div className="page create-preview-page">
      <div className="create-preview-head">
        <button className="btn btn-ghost btn-sm" onClick={() => onClose(session)}>
          <Icon name="chev_l" size={14} /> {t('create.back')}
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="eyebrow">{statusLabel} · {batch.id?.slice(-6)}</div>
          <h2 className="h2 create-preview-title">
            {topic || batch.topic || t('create.newBatch')}
          </h2>
          <div className="caption" style={{ marginTop: 4 }}>
            {t('create.readyOfCount', { ready: readyCount, total: plannedCount })}
            {failedCount > 0 ? t('create.failedSuffix', { value: failedCount }) : ''}
          </div>
          <div className="batch-header-progress"><div style={{ width: `${progress}%` }} /></div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => onViewInLibrary(session)}>
          <Icon name="grid" size={12} /> {t('create.openInLibrary')}
        </button>
      </div>

      <BatchPreview
        posts={livePosts}
        platforms={plannedPlatforms}
        targets={targets}
        topic={topic}
        batch={displayBatch}
        onEdit={onOpenPost}
        onPublishNow={(post, intent) => onOpenPost(post, intent)}
      />
    </div>
  );
}

function PreviewResumeCard({ session, onResume, onDismiss, onViewInLibrary }) {
  const { t } = useTranslation();
  const statusClass = session.statusLabel === 'Scheduled' ? 'chip-blue' : session.terminal ? 'chip-green' : 'chip-blue';
  return (
    <div className="create-resume-card">
      <div className="create-resume-main">
        <span className={'chip ' + statusClass}>{session.statusLabel}</span>
        <div>
          <div className="create-resume-title">{session.topic}</div>
          <div className="caption">
            {t('create.readyOfCount', { ready: session.readyCount, total: session.plannedCount })}
            {session.failedCount > 0 ? t('create.failedSuffix', { value: session.failedCount }) : ''}
            {session.terminal ? t('create.previewFrozen') : t('create.stillUpdating')}
          </div>
        </div>
      </div>
      <div className="batch-header-progress create-resume-progress"><div style={{ width: `${session.progress}%` }} /></div>
      <div className="create-resume-actions">
        <button className="btn btn-secondary btn-sm" onClick={onResume}><Icon name="play" size={12} /> {t('create.resumePreview')}</button>
        <button className="btn btn-ghost btn-sm" onClick={() => onViewInLibrary(session)}><Icon name="grid" size={12} /> {t('create.openLibrary')}</button>
        <button className="btn btn-icon btn-ghost" onClick={onDismiss} aria-label={t('create.dismissPreview')}><Icon name="x" size={13} /></button>
      </div>
    </div>
  );
}

function previewProgress(batch, posts, plannedCount) {
  const batchProgress = Number(batch?.progress);
  const total = Math.max(1, plannedCount || posts.length || Number(batch?.totalItems || 1));
  const done = posts.filter(post => ['ready', 'scheduled', 'publishing', 'published', 'failed'].includes(post.status)).length;
  const rowProgress = Math.round((done / total) * 100);
  const progress = Number.isFinite(batchProgress) && batchProgress > 0 ? Math.max(batchProgress, rowProgress) : rowProgress;
  return Math.max(5, Math.min(100, Math.round(progress)));
}

function deriveResumeSession(session, content) {
  if (!session?.batch) return null;
  const batchId = session.batch.id;
  const matching = content
    .filter(item => item.generationBatchId === batchId)
    .map(item => normalizePost(item.raw || item))
    .filter(Boolean);
  const posts = mergeStablePreviewPosts(session.posts || [], matching);
  const displayPosts = posts.length ? posts : session.posts || [];
  const batch = mergePreviewBatch(session.batch, matching.length ? content.find(item => item.generationBatchId === batchId)?.batch : null);
  const plannedCount = session.plannedCount || Math.max(1, displayPosts.length);
  const readyCount = displayPosts.filter(post => ['ready', 'scheduled', 'publishing', 'published'].includes(post.status)).length;
  const failedCount = displayPosts.filter(post => post.status === 'failed').length;
  const terminal = previewIsTerminal(batch, displayPosts, plannedCount);
  const progress = previewProgress(batch, displayPosts, plannedCount);
  return {
    ...session,
    batch,
    posts: displayPosts,
    statusLabel: previewBatchStatusLabel(batch, displayPosts),
    progress,
    readyCount,
    failedCount,
    plannedCount,
    terminal,
  };
}

function previewIsTerminal(batch, posts, plannedCount) {
  if (!posts.length || posts.length < plannedCount) return false;
  if (posts.some(post => ['generating', 'publishing'].includes(post.status) || isScheduledSoonPreviewPost(post))) return false;
  return posts.every(post => ['ready', 'scheduled', 'published', 'failed'].includes(post.status));
}

function isScheduledSoonPreviewPost(post) {
  if (post?.status !== 'scheduled' || !post.publishScheduleAt) return false;
  const timestamp = Date.parse(post.publishScheduleAt);
  return Number.isFinite(timestamp) && timestamp <= Date.now() + CREATE_PREVIEW_PUBLISH_LOOKAHEAD_MS;
}

const ACTIVE_PREVIEW_BATCH_STATUSES = new Set(['queued', 'generating', 'reserved']);

function mergeStablePreviewPosts(previous, incoming) {
  if (!previous.length) return incoming;
  if (!incoming.length) return previous;
  const byId = new Map(previous.map(post => [post.id, post]));
  incoming.forEach(post => {
    const current = byId.get(post.id);
    byId.set(post.id, pickMoreCompletePreviewPost(current, post));
  });
  return incoming.map(post => byId.get(post.id) || post);
}

function pickMoreCompletePreviewPost(previous, next) {
  if (!previous) return next;
  if (!next) return previous;
  if (previewStatusRank(previous.status) > previewStatusRank(next.status)) return previous;
  if (previewStatusRank(next.status) > previewStatusRank(previous.status)) return next;
  const previousSlideCount = Array.isArray(previous.slides) ? previous.slides.length : 0;
  const nextSlideCount = Array.isArray(next.slides) ? next.slides.length : 0;
  if (previousSlideCount > nextSlideCount) return previous;
  return next;
}

function previewStatusRank(status) {
  if (status === 'published') return 6;
  if (status === 'failed') return 5;
  if (status === 'publishing') return 4;
  if (status === 'scheduled') return 3;
  if (status === 'ready') return 2;
  if (status === 'generating') return 1;
  return 0;
}

function mergePreviewBatch(previous, next) {
  if (!previous) return next;
  if (!next) return previous;
  const previousProgress = Number(previous.progress || 0);
  const nextProgress = Number(next.progress || 0);
  return {
    ...previous,
    ...next,
    status: previewStatusRank(previous.status) > previewStatusRank(next.status) ? previous.status : next.status,
    progress: Math.max(previousProgress, nextProgress),
    readyCount: Math.max(Number(previous.readyCount || 0), Number(next.readyCount || 0)),
    generatingCount: Math.min(Number(previous.generatingCount || 0), Number(next.generatingCount || 0)),
  };
}

function pendingPostProgress(batch, index, plannedCount) {
  const batchProgress = Number(batch?.progress);
  if (Number.isFinite(batchProgress) && batchProgress > 0) {
    const total = Math.max(1, plannedCount || Number(batch?.totalItems || 1));
    const perPostOffset = Math.min(12, Math.round((index / total) * 12));
    return Math.max(8, Math.min(95, Math.round(batchProgress - perPostOffset)));
  }
  return 8;
}

function previewBatchStatusLabel(batch, posts) {
  if (hasFutureScheduledPreview(batch, posts)) return 'Scheduled';
  if (posts.some(post => post.status === 'scheduled')) return 'Scheduled';
  if (posts.some(post => post.status === 'publishing')) return 'Publishing';
  if (posts.length && posts.every(post => post.status === 'published')) return 'Published';
  if (posts.some(post => post.status === 'published')) return 'Publishing';
  if (posts.some(post => post.status === 'failed')) return 'Failed';
  if (batch?.status === 'ready' || posts.every(post => post.status === 'ready')) return 'Ready';
  return 'Generating';
}

function hasFutureScheduledPreview(batch, posts) {
  return futureTime(batch?.scheduleStartAt) || posts.some(post => futureTime(post?.publishScheduleAt));
}

function isContentPlanPreviewBatch(batch) {
  return batch?.goal === 'Content Plan weekly posts' || /^Content Plan week starting /i.test(batch?.contextNote || '');
}

function futureTime(value) {
  const timestamp = Date.parse(value || '');
  return Number.isFinite(timestamp) && timestamp > Date.now();
}

const Toggle = ({ val, on, label }) => (
  <button onClick={on} className={'tgl ' + (val ? 'on' : '')}>
    <span className="tgl-knob" />
    <span>{label}</span>
  </button>
);

function normalizePlatform(platform) {
  return platform === 'twitter' ? 'x' : platform;
}

function creditLabel(usage) {
  if (!usage) return '--';
  if (usage.remaining === null || usage.limit === null) return 'Unlimited';
  return String(usage.remaining);
}

function isMissingBrandProfileError(error) {
  return error?.status === 404 && /brand profile/i.test(error?.message || '');
}

function referenceContextSuffix(item) {
  if (item.url) return ` · ${referenceUrlHost(item.url)}`;
  if (item.fileName) return ` · ${item.fileName}`;
  return '';
}

function referenceUrlHost(value) {
  try {
    return new URL(value).hostname.replace(/^www\./, '');
  } catch {
    return 'link';
  }
}

function normalizeReferenceUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    const url = new URL(raw.startsWith('http://') || raw.startsWith('https://') ? raw : `https://${raw}`);
    if (!['http:', 'https:'].includes(url.protocol)) return '';
    url.hash = '';
    return url.href;
  } catch {
    return '';
  }
}

function extractHttpUrls(value) {
  const matches = String(value || '').match(/https?:\/\/[^\s<>()"']+/gi) || [];
  return uniqueStrings(matches.map(item => normalizeReferenceUrl(item.replace(/[.,;:!?]+$/, ''))).filter(Boolean)).slice(0, 8);
}

function uniqueStrings(values) {
  return [...new Set((values || []).filter(Boolean))];
}

function uniqueFiles(files) {
  const seen = new Set();
  return files.filter(file => {
    const key = `${file.name}:${file.size}:${file.lastModified}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function uniqueReferenceContexts(items) {
  const seen = new Set();
  return items.filter(item => {
    const key = item.assetId || item.url || item.id;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 8);
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('Unable to read reference file'));
    reader.readAsDataURL(file);
  });
}

function contentPlanSuggestionItems(savedStrategy) {
  const strategy = savedStrategy?.strategy || null;
  if (!strategy) return [];
  const items = [];
  addSuggestionStrings(items, strategy.quickSuggestions, {});
  addPostSuggestions(items, strategy.nextBestPosts);
  if (strategy.firstPostRecommendation?.topic) {
    items.push({
      topic: strategy.firstPostRecommendation.topic,
      label: suggestionLabel(strategy.firstPostRecommendation.topic),
      platform: normalizePlatform(strategy.firstPostRecommendation.platform),
      goal: strategy.firstPostRecommendation.goal,
    });
  }
  addSuggestionStrings(items, strategy.contentAngles, {});
  Object.entries(strategy.platformInstructions || {}).forEach(([platform, instructions]) => {
    addSuggestionStrings(items, instructions?.samplePostAngles, { platform: normalizePlatform(platform) });
  });
  Object.entries(strategy.platformStrategy || {}).forEach(([platform, plan]) => {
    addSuggestionStrings(items, plan?.samplePostAngles, { platform: normalizePlatform(platform) });
  });

  const seen = new Set();
  return items
    .filter(item => {
      const topic = String(item.topic || '').trim();
      if (!topic) return false;
      const key = topic.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      item.topic = topic;
      item.label = item.label || suggestionLabel(topic);
      return true;
    })
    .slice(0, 6);
}

function addSuggestionStrings(items, values, defaults) {
  if (!Array.isArray(values)) return;
  values.forEach(value => {
    const topic = suggestionTopic(value);
    if (!topic) return;
    items.push({
      ...defaults,
      topic,
      label: suggestionLabel(topic),
    });
  });
}

function addPostSuggestions(items, posts) {
  if (!Array.isArray(posts)) return;
  posts.forEach(post => {
    const topic = suggestionTopic(post?.topic || post?.title || post?.angle);
    if (!topic) return;
    items.push({
      topic,
      label: suggestionLabel(topic),
      platform: normalizePlatform(post?.platform),
      goal: post?.goal,
    });
  });
}

function suggestionTopic(value) {
  if (typeof value === 'string') return value.trim();
  if (value && typeof value === 'object') {
    return suggestionTopic(value.topic || value.title || value.angle || value.name);
  }
  return '';
}

function suggestionLabel(value) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  return text.length > 42 ? `${text.slice(0, 39).trim()}...` : text;
}

function isKnownGoal(goal) {
  return GOAL_OPTIONS.some(([value]) => value === goal);
}

function normalizePlatforms(platforms) {
  return [...new Set((platforms || []).map(normalizePlatform).filter(Boolean))];
}

function normalizeIds(ids) {
  return [...new Set((ids || []).filter(Boolean))];
}

function defaultSelectedTargetIds(targetRows, previous, selectedPlatforms) {
  const selectedPlatformSet = new Set(selectedPlatforms);
  const readyIds = targetRows
    .filter(row => row.ready && selectedPlatformSet.has(row.platform))
    .map(row => row.id);
  const retained = previous.filter(id => readyIds.includes(id));
  return normalizeIds([...retained, ...readyIds]);
}

function publishTargetBlockedReason(account, registry) {
  const platform = normalizePlatform(account.platform);
  if (account.status !== 'connected') return 'Reconnect required before publishing';
  if (registry[platform] && !registry[platform].supportsPublish) return 'Publishing not enabled for this platform';
  if (platform === 'telegram' && account.capabilities?.destinationType && account.capabilities.destinationType !== 'channel') {
    return 'Telegram publishing requires a connected channel';
  }
  return null;
}

function displayAccountName(account) {
  const value = (account.displayName || '').trim();
  if (value && !isOpaqueProviderName(value, account.externalAccountId)) return value;
  if (account.platform === 'instagram' && account.capabilities?.instagramUsername) {
    return `@${String(account.capabilities.instagramUsername).replace(/^@/, '')}`;
  }
  if (account.platform === 'instagram') return 'Instagram account';
  if (account.platform === 'threads') return 'Threads account';
  if (account.platform === 'facebook') return 'Facebook Page';
  if (account.platform === 'telegram') return 'Telegram channel';
  if (normalizePlatform(account.platform) === 'x') return 'X account';
  return platformLabel(account.platform);
}

function accountSecondaryText(account, displayName) {
  if (account.platform === 'instagram' && displayName === 'Instagram account') return 'Username unavailable from Instagram Login';
  const connectedAt = formatDate(account.connectedAt);
  const types = account.types || 'Connected account';
  return connectedAt ? `Ready since ${connectedAt} · ${types}` : `Ready · ${types}`;
}

function isOpaqueProviderName(value, externalAccountId) {
  return /^\d{8,}$/.test(value) || (externalAccountId && value === externalAccountId);
}

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return formatAppDate(date, { month: 'short', day: 'numeric' });
}

function platformLabel(platform) {
  const labels = { instagram: 'Instagram', threads: 'Threads', telegram: 'Telegram', x: 'X', twitter: 'X', tiktok: 'TikTok', linkedin: 'LinkedIn', facebook: 'Facebook Page' };
  return labels[platform] || platform;
}

function clampCount(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 1;
  return Math.min(Math.max(Math.floor(number), 1), 30);
}

function plannedCountForBatch(batch, fallback) {
  const total = Number(batch?.totalItems);
  return Number.isFinite(total) && total > 0 ? total : fallback;
}

const LIBRARY_FILTER_STATUSES = new Set(['generating', 'ready', 'scheduled', 'published', 'draft', 'failed']);
const ACTIVE_LIBRARY_BATCH_STATUSES = new Set(['queued', 'generating', 'reserved']);

function libraryFilterForPreview(session, fallbackBatch, content) {
  const batch = session?.batch || fallbackBatch || null;
  const batchId = batch?.id;
  const batchItems = batchId ? content.filter(item => item.generationBatchId === batchId) : [];
  const statuses = batchItems.length ? batchItems.map(item => item.status) : (session?.posts || []).map(post => post?.status);
  const filter = libraryFilterForStatuses(statuses);
  if (filter) return filter;
  if (!statuses.length && ACTIVE_LIBRARY_BATCH_STATUSES.has(batch?.status)) return 'generating';
  return null;
}

function libraryFilterForStatuses(statuses) {
  const filters = [...new Set(statuses.map(normalizeLibraryFilterStatus).filter(Boolean))];
  return filters.length === 1 ? filters[0] : null;
}

function normalizeLibraryFilterStatus(status) {
  return LIBRARY_FILTER_STATUSES.has(status) ? status : null;
}

function scheduleDateForPreset(preset, customValue) {
  const now = new Date();
  if (preset === 'soon') return new Date(now.getTime() + 60 * 1000);
  if (preset === '15m') return new Date(now.getTime() + 15 * 60 * 1000);
  if (preset === 'best') return bestTimeDate(now);
  if (preset === 'evening') {
    const evening = new Date(now);
    evening.setHours(18, 0, 0, 0);
    if (evening <= now) evening.setDate(evening.getDate() + 1);
    return evening;
  }
  if (preset === 'tomorrow') {
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    return tomorrow;
  }
  if (preset === 'custom' && customValue) {
    const custom = new Date(customValue);
    if (!Number.isNaN(custom.getTime()) && custom > now) return custom;
  }
  return new Date(now.getTime() + 15 * 60 * 1000);
}

function bestTimeDate(now = new Date()) {
  const best = new Date(now);
  best.setHours(10, 0, 0, 0);
  if (best <= now) {
    best.setDate(best.getDate() + 1);
    best.setHours(10, 0, 0, 0);
  }
  return best;
}

function toDatetimeLocalValue(date) {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return '';
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}
